'use client';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import enTranslations from './i18n/en.json';
import arTranslations from './i18n/ar.json';

type Lang = 'en' | 'ar';
type Dict = any;

const dicts: Record<Lang, Dict> = { 
  en: enTranslations, 
  ar: arTranslations 
};

// Helper function to get nested object values
function getNestedValue(obj: any, path: string): string {
  return path.split('.').reduce((current, key) => current?.[key], obj) || path;
}

const I18nCtx = createContext<{ lang: Lang; setLang: (l:Lang)=>void; t:(k:string)=>string; translations: Dict }>({
  lang: 'en', setLang: () => {}, t: (k) => k, translations: enTranslations,
});

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLang] = useState<Lang>((typeof window !== 'undefined' && (localStorage.getItem('lang') as Lang)) || 'en');

  useEffect(() => {
    localStorage.setItem('lang', lang);
    const root = document.documentElement;
    root.lang = lang;
    root.dir = lang === 'ar' ? 'rtl' : 'ltr';
  }, [lang]);

  const t = useMemo(() => {
    const d = dicts[lang] || dicts.en;
    return (k: string) => getNestedValue(d, k) ?? k;
  }, [lang]);

  const translations = useMemo(() => dicts[lang] || dicts.en, [lang]);

  return <I18nCtx.Provider value={{ lang, setLang, t, translations }}>{children}</I18nCtx.Provider>;
}

export const useI18n = () => useContext(I18nCtx);